import { sxAppend } from '@/libs/material/sx';
import { CirclePartnerHealthResource } from '@calm/evexia-web-sdk';
import { Banner } from '@rhg/components/banner';
import {
    Box,
    Card,
    CardActionArea,
    Divider,
    Typography,
    CardHeader,
    SxProps,
    Theme,
} from '@rhg/libs/material';
import Image from '@rhg/libs/next/image';
import { useIntl } from 'react-intl';
import messages from '../messages';

const styleBanner = {
    my: 6,
    width: '100%',
};

const styleDivider = {
    mb: 4,
};

const styleCardContentBox = {
    ml: 4,
    mb: 11,
};

const styleCardHeader = {
    p: 0,
    mt: 2,

    span: {
        fontSize: 16,
    },
};

const styleCardActionArea = {
    '.MuiCardActionArea-focusHighlight': {
        background: 'transparent',
    },
};

interface ResourceCardProps {
    resource: CirclePartnerHealthResource;
    onClick: () => void;
    variant?: 'banner' | 'card';
    isDynamic?: boolean;
    sx?: SxProps<Theme>;
}

export default function ResourceCard({
    resource,
    onClick,
    variant = 'banner',
    isDynamic,
    sx,
}: ResourceCardProps) {
    const { formatMessage } = useIntl();

    return (
        <>
            {variant === 'card' ? (
                <Card>
                    <CardActionArea
                        trackButton="Resource"
                        trackProps={{
                            ResourceName: resource.name,
                            ResourceCategory: resource.category,
                        }}
                        data-testid={`resource-card-item-${resource.id}`}
                        onClick={onClick}
                        aria-label={formatMessage(
                            messages['resources.carousel.card.aria_label'],
                            { resourceName: resource.name }
                        )}
                        sx={sxAppend(styleCardActionArea, sx)}
                    >
                        {resource.logoUrl && (
                            <>
                                {isDynamic ? (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            overflow: 'hidden',
                                            '& img': {
                                                aspectRatio: '1',
                                                width: '100%',
                                            },
                                        }}
                                    >
                                        <Image
                                            src={resource.logoUrl}
                                            alt={
                                                resource.name || 'Resource Logo'
                                            }
                                            width={1024}
                                            height={1024}
                                            unoptimized // CDN doesn't optimize SVGs, will 404 without this prop
                                            style={{
                                                objectFit: 'cover',
                                                height: '100%',
                                                width: '100%',
                                            }}
                                        />
                                    </Box>
                                ) : (
                                    <Image
                                        height={326}
                                        width={326}
                                        src={resource.logoUrl}
                                        alt=""
                                        unoptimized // CDN doesn't optimize SVGs, will 404 without this prop
                                        objectFit="contain"
                                        style={{ display: 'block' }}
                                    />
                                )}
                            </>
                        )}
                        <Divider sx={styleDivider} />
                        <Box sx={styleCardContentBox}>
                            <Typography variant="caption" color="text.sub">
                                {resource.category}
                            </Typography>
                            <CardHeader
                                data-testid="resource-card-header"
                                sx={styleCardHeader}
                                title={resource.name}
                            />
                        </Box>
                    </CardActionArea>
                </Card>
            ) : (
                <Banner
                    testId="resource-banner"
                    sx={styleBanner}
                    leftImage={
                        resource.logoUrl && (
                            <Image
                                src={resource.logoUrl}
                                alt=""
                                objectFit="contain"
                                unoptimized // CDN doesn't optimize SVGs, will 404 without this prop
                                fill
                                style={{ display: 'block' }}
                            />
                        )
                    }
                    title={resource.name || ''}
                    subtitle={resource.bannerDescription}
                    primaryAction={{
                        actionText: formatMessage(
                            messages['resources.learn_more']
                        ),
                        onClickAction: onClick,
                        trackAction: 'Resource',
                        trackProps: {
                            ResourceName: resource.name,
                            ResourceCategory: resource.category,
                        },
                    }}
                />
            )}
        </>
    );
}
